import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@tanstack/react-query";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarTodayIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Close as CloseIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Numbers as NumbersIcon,
} from "@mui/icons-material";
import CustomerService from "../../../../services/Customer";
import {
  defaultStorefronts,
  personas as personaOptions,
  mockPending,
} from "../test-data";

const CircleIcon = ({ children, size = "2.5rem" }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: size,
      height: size,
      borderRadius: "50%",
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "action.hover",
      color: "text.secondary",
      flex: "0 0 auto",
    }}
  >
    {children}
  </Box>
);

const CommentsPanel = ({ text }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "#E3F2FD",
      borderRadius: 2,
      p: 1.5,
    }}
  >
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      <CircleIcon>
        <ChatBubbleOutlineIcon sx={{ fontSize: "1.125rem" }} />
      </CircleIcon>

      <Typography
        sx={{ fontWeight: 700, color: "text.primary", fontSize: "1.3rem" }}
      >
        Comments
      </Typography>
    </Box>
    <Typography color="text.secondary" fontSize={"1.125rem"}>
      {text}
    </Typography>
  </Box>
);

const PendingApprovals = () => {
  const [users, setUsers] = useState(mockPending);
  const [searchTerm, setSearchTerm] = useState("");
  const [userSelections, setUserSelections] = useState({});
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const { data: customerOptions = [], isLoading: loadingCustomers } = useQuery({
    queryKey: ["customerList"],
    queryFn: CustomerService.fetchCustomers,
  });

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.company.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const handlePersonaChange = (userId, persona) => {
    setUserSelections((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), persona },
    }));
  };

  const handleStorefrontChange = (userId, storefront) => {
    setUserSelections((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), storefront },
    }));
  };

  const handleCustomersChange = (userId, customers) => {
    setUserSelections((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), customers },
    }));
  };

  const handleProcessUser = (user) => {
    const selections = userSelections[user.id];
    if (!selections?.persona) return;
    if (!selections?.customers || selections.customers.length === 0) return;

    setUsers((prev) => prev.filter((u) => u.id !== user.id));
    // Replace with API call if needed
    // eslint-disable-next-line no-console
    console.log("Processing pending user:", {
      userId: user.id,
      persona: selections.persona,
      storefront:
        selections.storefront ||
        user.storefront ||
        defaultStorefronts[0]?.value,
      customerIds: selections.customers.map((c) => c.id ?? c.externalId),
    });

    setUserSelections((prev) => {
      const { [user.id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const UserRow = ({ user }) => {
    const isExpanded = expandedUsers.has(user.id);

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            borderRadius: 1,
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: "action.hover" },
            px: { md: 1 },
            py: { md: 0.5 },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flex={1}
            minWidth={0}
            onClick={() => toggleUserExpanded(user.id)}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleUserExpanded(user.id);
              }
            }}
            sx={{ cursor: "pointer" }}
          >
            <IconButton size="medium" sx={{ p: 0.5, pointerEvents: "none" }}>
              {isExpanded ? (
                <KeyboardArrowDownIcon fontSize="medium" />
              ) : (
                <KeyboardArrowRightIcon fontSize="medium" />
              )}
            </IconButton>

            {/* User Info */}
            <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
              <CircleIcon>
                <PersonIcon color="action" fontSize="small" />
              </CircleIcon>

              <Typography fontWeight={600} fontSize={"1.125rem"} noWrap>
                {/* Full Name */}
                {user.firstName} {user.lastName}
                {/* Mobile-only extra details (email) */}
                <Box
                  display={{ xs: "flex", md: "none" }}
                  alignItems="center"
                  gap={0.75}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={"1.125rem"}
                    noWrap
                  >
                    {user.email}
                  </Typography>
                </Box>
              </Typography>
            </Box>

            <Box
              display={{ xs: "none", md: "flex" }}
              alignItems="center"
              gap={2}
              minWidth={0}
            >
              {/* Email */}
              <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                <CircleIcon>
                  <EmailIcon color="action" fontSize="small" />
                </CircleIcon>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"1.125rem"}
                  noWrap
                >
                  {user.email}
                </Typography>
              </Box>

              <Box
                display={{ xs: "none", lg: "flex" }}
                alignItems="center"
                gap={2}
                minWidth={0}
              >
                {/* Customer Number */}
                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                  <CircleIcon>
                    <NumbersIcon color="action" fontSize="small" />
                  </CircleIcon>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={"1.125rem"}
                    noWrap
                  >
                    {user.customerNumber || "N/A"}
                  </Typography>
                </Box>

                {/* Company */}
                <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
                  <CircleIcon>
                    <BusinessIcon color="action" fontSize="small" />
                  </CircleIcon>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={"1.125rem"}
                    noWrap
                  >
                    {user.company}
                  </Typography>
                </Box>

                {/* Signup Date */}
                <Box display="flex" alignItems="center" gap={0.75}>
                  <CircleIcon>
                    <CalendarTodayIcon color="action" fontSize="small" />
                  </CircleIcon>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize={"1.125rem"}
                  >
                    {new Date(user.signupDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* User Status */}
          <Chip
            size="small"
            label="Pending"
            clickable
            onClick={() => toggleUserExpanded(user.id)}
            sx={{
              cursor: "pointer",
              fontSize: "1.125rem",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: "action.hover" },
            }}
          />
        </Box>

        {isExpanded ? (
          <Box
            pt={2}
            mt={2}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 3,
              mt: 2,
              position: "relative",
            }}
          >
            {/* Form Section Header */}
            <Box
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: "1.2rem",
                }}
              >
                User Assignment Configuration
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  mt: 0.5,
                }}
              >
                Configure persona, storefront, and customer assignments for this
                user
              </Typography>
            </Box>

            {/* Mobile-only extra details (company/date) shown before selects */}
            <Box
              display={{ xs: "grid", lg: "none" }}
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={1.25}
              mb={1.5}
            >
              {/* Customer Number */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                minWidth={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                  borderRadius: 2,
                  px: 1.25,
                  py: 1,
                }}
              >
                <CircleIcon>
                  <NumbersIcon color="action" fontSize="small" />
                </CircleIcon>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  fontSize={"1.125rem"}
                >
                  {user.customerNumber || "N/A"}
                </Typography>
              </Box>

              {/* Company */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                minWidth={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                  borderRadius: 2,
                  px: 1.25,
                  py: 1,
                }}
              >
                <CircleIcon>
                  <BusinessIcon color="action" fontSize="small" />
                </CircleIcon>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  fontSize={"1.125rem"}
                >
                  {user.company}
                </Typography>
              </Box>

              {/* Signup Date */}
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "action.hover",
                  borderRadius: 2,
                  px: 1.25,
                  py: 1,
                }}
              >
                <CircleIcon>
                  <CalendarTodayIcon color="action" fontSize="small" />
                </CircleIcon>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"1.125rem"}
                >
                  {new Date(user.signupDate).toLocaleDateString()}
                </Typography>
              </Box>

              {/* Comments */}
              {user.comments ? (
                <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                  <CommentsPanel text={user.comments} />
                </Box>
              ) : null}
            </Box>

            {/* Comments */}
            {user.comments ? (
              <Box display={{ xs: "none", lg: "block" }} mb={1.5}>
                <CommentsPanel text={user.comments} />
              </Box>
            ) : null}

            {/* Form Fields Section */}
            <Box
              sx={{
                backgroundColor: "action.hover",
                borderRadius: 1,
                p: 2,
                mb: 2,
              }}
            >
              {/* Assign Persona and Storefront */}
              <Box
                display={{ xs: "grid" }}
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={2}
                mb={2}
              >
                <Box>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontSize: "14px" }}>
                      Assign Persona
                    </InputLabel>

                    <Select
                      label="Assign Persona"
                      value={userSelections[user.id]?.persona || ""}
                      onChange={(e) =>
                        handlePersonaChange(user.id, e.target.value)
                      }
                      sx={{
                        fontSize: "14px",
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                      }}
                    >
                      {personaOptions.map((p) => (
                        <MenuItem
                          key={p}
                          value={p}
                          style={{
                            fontSize: 14,
                            paddingTop: 10,
                            paddingBottom: 10,
                          }}
                        >
                          {p}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <FormControl fullWidth>
                    <InputLabel sx={{ fontSize: "14px" }}>
                      Assign Storefront
                    </InputLabel>

                    <Select
                      label="Assign Storefront"
                      value={
                        userSelections[user.id]?.storefront || user.storefront
                      }
                      onChange={(e) =>
                        handleStorefrontChange(user.id, e.target.value)
                      }
                      sx={{
                        fontSize: "14px",
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                      }}
                    >
                      {defaultStorefronts.map((s) => (
                        <MenuItem
                          key={s.value}
                          value={s.value}
                          style={{
                            fontSize: 14,
                            paddingTop: 10,
                            paddingBottom: 10,
                          }}
                        >
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Assign Customers */}
              <Box mt={2}>
                <Autocomplete
                  multiple
                  options={customerOptions}
                  loading={loadingCustomers}
                  value={userSelections[user.id]?.customers || []}
                  onChange={(_, newValue) =>
                    handleCustomersChange(user.id, newValue)
                  }
                  getOptionLabel={(option) =>
                    `${option.externalId || option.id || ""} - ${
                      option.name || ""
                    }`
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option.id ?? option.externalId) ===
                    (value.id ?? value.externalId)
                  }
                  filterSelectedOptions
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        fontSize: "1.2rem",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {option.externalId || option.id || ""} -{" "}
                      {option.name || ""}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign Customers"
                      placeholder={
                        loadingCustomers
                          ? "Loading customers..."
                          : "Search and select customers"
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "14px",
                          backgroundColor: "white",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "divider",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "14px",
                        },
                        "& .MuiChip-root": {
                          fontSize: "1.25rem",
                        },
                      }}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCustomers ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Save Changes */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                size="medium"
                onClick={() => handleProcessUser(user)}
                disabled={
                  !userSelections[user.id]?.persona ||
                  !userSelections[user.id]?.customers ||
                  (userSelections[user.id]?.customers?.length || 0) === 0
                }
                sx={{
                  fontSize: "14px",
                  width: { xs: "100%", lg: "auto" },
                }}
              >
                Approve User
              </Button>
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: { lg: "1200px", xl: "1400px" }, mx: "auto" }}>
      <Card variant="outlined">
        <CardContent>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              flex: { xs: "none", lg: "none" },
              width: { xs: "100%", lg: "300px", xl: "400px" },
              mb: 2,

              "& .MuiOutlinedInput-root": {
                height: "40px",
                fontSize: 14,

                "& .MuiInputBase-input": {
                  height: "40px",
                },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#666", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ color: "#666" }}
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {filteredUsers.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography color="text.secondary" sx={{ fontSize: "1.125rem" }}>
                No pending users found
              </Typography>
            </Box>
          ) : (
            <Box display="grid" gap={2}>
              {filteredUsers.map((user) => (
                <UserRow key={user.id} user={user} />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PendingApprovals;
